import ReactAsyncSelect from 'react-select/async';
import isNil from 'lodash/isNil';
import classnames from 'classnames';
import type { RefObject } from 'react';

import '../Select_.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { LeftTitle } from '@components/LeftTitle';
import { NotValidFieldText } from '@components/NotValidFieldText';
import {
    ClearIndicator,
    DropdownIndicator,
    MenuList,
    noOptionsMessage,
    Option,
    OptionType,
    SingleValue,
} from '../Select';
import { translate } from '@chaika/i18n/helper';
import { normalizeYoFilter, normalizeYoSearch, normalizeYoString } from '@utils/normalizeYoString';
import { getMenuPortalProps } from '../getMenuPortalProps';
import { useAsyncSelect } from './hooks/useAsyncSelect';
import type { DefaultOption } from '../constants/constants';

// https://github.com/JedWatson/react-select#props
export interface AsyncSelectProps {
    // The default set of options to show before the user starts searching. When
    // set to `true`, the results for loadOptions('') will be autoloaded.
    defaultOptions?: OptionType[] | boolean;
    dataTest?: string;
    leftTitle?: boolean;
    options?: OptionType[];
    title?: string;
    name?: string;
    inputRef?: RefObject<HTMLInputElement | null>;
    value?: OptionType;
    placeholder?: string;
    disabled?: boolean;
    locked?: boolean;
    isClearable?: boolean;
    isLoading?: boolean;
    isValid?: boolean;
    isRequired?: boolean;
    showValidStatus?: boolean;
    notValidText?: string;
    cacheOptions?: boolean;
    menuIsOpen?: boolean;
    hideSelectedOptions?: boolean;
    menuPortalTarget?: HTMLElement | null;
    selectedValue?: Nullable<string | number>;
    defaultOptionType?: DefaultOption;
    forcePlaceholder?: boolean;
    isFilteredOnServer?: boolean;
    // Function that returns a promise, which is the set of options to be used
    // once the promise resolves.
    loadOptions(inputValue: string): Promise<OptionType[]> | void;
    onMenuOpen?(): void;
    onChange(params: Nullable<OptionType>, name?: string): void;
    onInputChange?(value: string): void | Promise<void>;
}

export function AsyncSelect({
    title,
    name,
    dataTest,
    leftTitle,
    value,
    disabled,
    locked,
    onInputChange,
    cacheOptions = true,
    placeholder,
    defaultOptions,
    options,
    isClearable,
    isLoading,
    isValid,
    isRequired = false,
    showValidStatus = !isNil(isValid),
    notValidText,
    hideSelectedOptions,
    menuIsOpen,
    menuPortalTarget,
    loadOptions,
    onMenuOpen,
    onChange,
    selectedValue,
    defaultOptionType,
    forcePlaceholder,
    isFilteredOnServer = false,
}: AsyncSelectProps) {
    const {
        value: resolvedValue,
        defaultOptions: resolvedDefaultOptions,
        placeholder: resolvedPlaceholder,
        handleChange,
        handleFocus,
        handleBlur,
    } = useAsyncSelect({
        defaultOptions,
        defaultOptionType,
        selectedValue,
        value,
        placeholder,
        title,
        forcePlaceholder,
        name,
        onChange,
    });

    const select = (
        <div
            data-test={dataTest}
            data-testid={dataTest}
            className={classnames([
                'component_select__root',
                'component_select__root_alignedValue',
                locked && 'component_select__root_locked',
            ])}
        >
            {title && !leftTitle && (
                <FieldTitle
                    title={isRequired ? `${title} *` : title}
                    theme={FieldTitleTheme.Grey}
                />
            )}
            <ReactAsyncSelect<OptionType, false>
                menuIsOpen={menuIsOpen}
                cacheOptions={cacheOptions}
                defaultOptions={resolvedDefaultOptions}
                hideSelectedOptions={hideSelectedOptions}
                placeholder={resolvedPlaceholder ?? '-'}
                options={options}
                name={name}
                value={resolvedValue}
                loadOptions={(inputValue: string) =>
                    isFilteredOnServer
                        ? loadOptionsFilteredOnServer(inputValue, loadOptions)
                        : normalizeYoSearch(inputValue, loadOptions).then(items => items ?? [])
                }
                filterOption={isFilteredOnServer ? null : normalizeYoFilter}
                onChange={handleChange}
                isClearable={isClearable}
                isLoading={isLoading}
                onInputChange={onInputChange}
                loadingMessage={() => translate('loading')}
                noOptionsMessage={noOptionsMessage}
                isDisabled={disabled || locked}
                className={'component_select__root'}
                classNamePrefix={'react-select'}
                components={{ DropdownIndicator, MenuList, Option, SingleValue, ClearIndicator }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMenuOpen={onMenuOpen}
                {...getMenuPortalProps(menuPortalTarget, undefined)}
                {...({ locked } as any)}
            />
            {showValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
        </div>
    );

    return leftTitle && title ? <LeftTitle title={title}>{select}</LeftTitle> : select;
}

function loadOptionsFilteredOnServer(
    inputValue: string,
    loadOptions: AsyncSelectProps['loadOptions'],
): Promise<OptionType[]> {
    return Promise.resolve(loadOptions(normalizeYoString(inputValue))).then(items => items ?? []);
}
